import { useEffect, useState, useCallback } from 'react'
import { leadService } from '../services/leads'
import type { Lead, TelecallerWeight } from '../../../types'
import { UserRole, LeadStatus } from '../../../types'
import { LEAD_STATUS_COLORS, LEAD_STATUS_LABELS } from '../../../lib/constants'
import { authService } from '../../identity/services/auth'
import EnquiryTrackingCard from '../components/EnquiryTrackingCard'
import { Archive, Search, X, History, SlidersHorizontal, ChevronDown, Eye } from 'lucide-react'
import { useWindowWidth } from '../../../lib/useWindowWidth'

// ── Constants ─────────────────────────────────────────────────────────────────

const CLOSED_STATUS_OPTIONS: { label: string; value: string }[] = [
  { label: 'All Closed',     value: '' },
  { label: 'Lost',           value: LeadStatus.LOST },
  { label: 'Lost Elsewhere', value: LeadStatus.LOST_RENEWED_ELSEWHERE },
  { label: 'Not Responsive', value: LeadStatus.NOT_RESPONSIVE },
  { label: 'Policy Issued',  value: LeadStatus.POLICY_ISSUED },
]

const DATE_RANGE_OPTIONS = [
  { label: 'All time',      value: '' },
  { label: 'Last 7 days',   value: '7' },
  { label: 'Last 30 days',  value: '30' },
  { label: 'Last 90 days',  value: '90' },
]

function timeAgo(ts: string | null | undefined): string {
  if (!ts) return '—'
  const days = Math.floor((Date.now() - new Date(ts).getTime()) / 86_400_000)
  if (days === 0) return 'today'
  if (days === 1) return 'yesterday'
  return `${days}d ago`
}

function formatDate(ts: string | null | undefined): string {
  if (!ts) return '—'
  return new Date(ts).toLocaleDateString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
  })
}

// ── Mobile expandable cards ───────────────────────────────────────────────────

function MobileHistoryCards({
  leads, isManager, onView, onHistory,
}: {
  leads: Lead[]
  isManager: boolean
  onView: (id: number) => void
  onHistory: (id: number) => void
}) {
  const [expandedId, setExpandedId] = useState<number | null>(null)
  return (
    <div className="md:hidden overflow-y-auto h-full pb-2 space-y-2">
      {leads.map(lead => {
        const closedTs = lead.status_changed_at ?? lead.updated_at
        const expanded = expandedId === lead.id
        return (
          <div key={lead.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div
              className="flex items-center gap-2.5 px-3 py-3 cursor-pointer"
              onClick={() => setExpandedId(expanded ? null : lead.id)}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                  {lead.customer_name}
                </p>
                <p className="text-xs tabular-nums" style={{ color: 'var(--text-tertiary)' }}>
                  {lead.mobile}
                </p>
              </div>
              <span className={`${LEAD_STATUS_COLORS[lead.status] ?? 'badge-neutral'} shrink-0`} style={{ fontSize: 10 }}>
                {LEAD_STATUS_LABELS[lead.status] ?? lead.status}
              </span>
              <ChevronDown
                className="h-4 w-4 shrink-0 transition-transform"
                style={{ transform: expanded ? 'rotate(180deg)' : 'none', color: 'var(--text-tertiary)' }}
              />
            </div>
            {expanded && (
              <div style={{ borderTop: '1px solid var(--border-default)', padding: '10px 12px', background: 'var(--surface-subtle)' }}>
                {[lead.vehicle_reg_no, lead.vehicle_model].filter(Boolean).length > 0 && (
                  <div className="flex justify-between text-xs py-1">
                    <span style={{ color: 'var(--text-tertiary)' }}>Vehicle</span>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                      {[lead.vehicle_reg_no, lead.vehicle_model].filter(Boolean).join(' · ')}
                    </span>
                  </div>
                )}
                {isManager && lead.assigned_to_name && (
                  <div className="flex justify-between text-xs py-1">
                    <span style={{ color: 'var(--text-tertiary)' }}>Telecaller</span>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{lead.assigned_to_name}</span>
                  </div>
                )}
                <div className="flex justify-between text-xs py-1">
                  <span style={{ color: 'var(--text-tertiary)' }}>Closed</span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                    {formatDate(closedTs)} · {timeAgo(closedTs)}
                  </span>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    className="flex-1 flex items-center justify-center gap-1.5 text-xs py-2 rounded-md border"
                    style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)', background: 'var(--surface-card)' }}
                    onClick={e => { e.stopPropagation(); onHistory(lead.id) }}
                  >
                    <History className="h-3.5 w-3.5" /> History
                  </button>
                  <button
                    className="flex-1 flex items-center justify-center gap-1.5 text-xs py-2 rounded-md border"
                    style={{ borderColor: 'var(--brand-primary)', color: 'white', background: 'var(--brand-primary)' }}
                    onClick={e => { e.stopPropagation(); onView(lead.id) }}
                  >
                    <Eye className="h-3.5 w-3.5" /> View
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function LeadHistoryPage() {
  const [leads, setLeads]           = useState<Lead[]>([])
  const [total, setTotal]           = useState(0)
  const [page, setPage]             = useState(1)
  const [loading, setLoading]       = useState(true)
  const [telecallers, setTelecallers] = useState<TelecallerWeight[]>([])

  // Filters
  const [search, setSearch]         = useState('')
  const [statusFilter, setStatus]   = useState('')
  const [dateRange, setDateRange]   = useState('')
  const [tcFilter, setTcFilter]     = useState('')
  const isMobile = useWindowWidth() < 768
  const [filtersOpen, setFiltersOpen] = useState(!isMobile)

  // Slide-over
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null)

  const currentUser = authService.getCurrentUser()
  const isManager   = currentUser?.role !== UserRole.TELECALLER && currentUser?.role !== UserRole.BACKEND_ADMIN

  // Fetch telecaller list once for managers
  useEffect(() => {
    if (!isManager) return
    leadService.getTelecallerWeights()
      .then(setTelecallers)
      .catch(err => console.error('Failed to load telecallers:', err))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const activeFilterCount = [search, statusFilter, dateRange, tcFilter].filter(Boolean).length

  const fetchLeads = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, unknown> = {
        status_group: 'closed',
        sort_by: 'closed_date',
        page,
        page_size: 25,
      }
      if (statusFilter) params.status      = statusFilter
      if (tcFilter)     params.assigned_to = Number(tcFilter)
      // Pass search to backend for vehicle reg / mobile matching
      if (search)       params.search      = search

      const data = await leadService.list(params)
      let items: Lead[] = data.items

      // Client-side: also match customer name (encrypted server-side, decrypted in response)
      if (search) {
        const q = search.toLowerCase()
        items = items.filter(l =>
          (l.customer_name?.toLowerCase().includes(q)) ||
          (l.vehicle_reg_no?.toLowerCase().includes(q))
        )
      }

      // Client-side date filter (status_changed_at)
      if (dateRange) {
        const cutoff = Date.now() - parseInt(dateRange) * 86_400_000
        items = items.filter(l => {
          const ts = l.status_changed_at ?? l.updated_at
          return ts ? new Date(ts).getTime() >= cutoff : true
        })
      }

      setLeads(items)
      setTotal(data.total)
    } finally {
      setLoading(false)
    }
  }, [search, statusFilter, dateRange, tcFilter, page])

  useEffect(() => { fetchLeads() }, [fetchLeads])

  const resetPage = () => setPage(1)
  const totalPages = Math.max(1, Math.ceil(total / 25))

  const clearFilters = () => {
    setSearch('')
    setStatus('')
    setDateRange('')
    setTcFilter('')
    resetPage()
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">

      {/* ── Fixed header ───────────────────────────────────────────────── */}
      <div className="shrink-0 space-y-3 p-1 pb-0">
        <div className="section-header" style={{ marginBottom: 0 }}>
          <div className="flex items-center gap-2 flex-1">
            <Archive className="h-5 w-5 shrink-0" style={{ color: 'var(--text-tertiary)' }} />
            <div>
              <h1 className="page-title">Lead History</h1>
              <p className="page-subtitle">
                {currentUser?.role === UserRole.TELECALLER
                  ? 'Your closed leads'
                  : 'Closed leads across the team'}
                {' · '}{total.toLocaleString()} total
              </p>
            </div>
          </div>

        </div>

        {/* Row 1: search + filter toggle — always visible, always one line */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <Search
              className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none"
              style={{ color: 'var(--text-disabled)' }}
            />
            <input
              type="text"
              className="input pl-8 text-sm w-full"
              placeholder="Search name or reg…"
              value={search}
              onChange={e => { setSearch(e.target.value); resetPage() }}
            />
          </div>

          {/* Filter toggle — icon button with active-count badge */}
          <button
            onClick={() => setFiltersOpen(o => !o)}
            className="btn-icon shrink-0 relative"
            aria-label="Toggle filters"
            aria-expanded={filtersOpen}
            style={{
              color: activeFilterCount > 0 ? 'var(--brand-primary)' : 'var(--text-secondary)',
              border: `1px solid ${activeFilterCount > 0 ? 'var(--brand-primary)' : 'var(--border-default)'}`,
              background: activeFilterCount > 0 ? 'var(--brand-primary-subtle)' : 'transparent',
            }}
          >
            <SlidersHorizontal className="h-4 w-4" />
            {activeFilterCount > 0 && (
              <span
                className="absolute -top-1.5 -right-1.5 rounded-full flex items-center justify-center font-bold"
                style={{
                  width: 16, height: 16, fontSize: 10,
                  background: 'var(--brand-primary)', color: '#fff',
                }}
              >
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* Row 2: collapsible filter dropdowns */}
        {filtersOpen && (
          <div className="flex items-center gap-2 flex-wrap">
            <select
              className="input text-sm"
              value={statusFilter}
              onChange={e => { setStatus(e.target.value); resetPage() }}
            >
              {CLOSED_STATUS_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>

            <select
              className="input text-sm"
              value={dateRange}
              onChange={e => { setDateRange(e.target.value); resetPage() }}
            >
              {DATE_RANGE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>

            {isManager && (
              <select
                className="input text-sm"
                value={tcFilter}
                onChange={e => { setTcFilter(e.target.value); resetPage() }}
              >
                <option value="">All telecallers</option>
                {telecallers.map(tc => (
                  <option key={tc.user_id} value={tc.user_id}>{tc.name}</option>
                ))}
              </select>
            )}

            {activeFilterCount > 0 && (
              <button
                className="text-xs px-2.5 py-1 rounded-full border transition-colors"
                style={{ borderColor: 'var(--danger-text)', color: 'var(--danger-text)', background: 'transparent' }}
                onClick={clearFilters}
              >
                Clear all
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Content ────────────────────────────────────────────────────── */}
      <div className="flex-1 min-h-0 mt-3 overflow-hidden">
        {loading ? (
          <div className="table-card h-full flex items-center justify-center">
            <div className="empty-state"><div className="spinner" /></div>
          </div>
        ) : leads.length === 0 ? (
          <div className="table-card h-full flex items-center justify-center">
            <div className="empty-state">
              <Archive className="h-10 w-10 mb-3" style={{ color: 'var(--text-disabled)' }} />
              <p>No closed leads found</p>
            </div>
          </div>
        ) : (
          <>
            {/* ── Desktop table (md and up) ───────────────────────────── */}
            <div className="table-card h-full hidden md:flex flex-col min-h-0">
              <div className="overflow-auto flex-1">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Vehicle</th>
                      <th>Status</th>
                      {isManager && <th>Telecaller</th>}
                      <th>Closed</th>
                      <th>Days ago</th>
                      <th>History</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map(lead => {
                      const closedTs = lead.status_changed_at ?? lead.updated_at
                      return (
                        <tr
                          key={lead.id}
                          className="cursor-pointer"
                          onClick={() => window.location.href = `/leads/${lead.id}`}
                        >
                          <td>
                            <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                              {lead.customer_name}
                            </p>
                            <p className="text-xs tabular-nums" style={{ color: 'var(--text-tertiary)' }}>
                              {lead.mobile}
                            </p>
                          </td>
                          <td>
                            <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                              {lead.vehicle_reg_no || '—'}
                            </p>
                            <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                              {lead.vehicle_model || ''}
                            </p>
                          </td>
                          <td>
                            <span className={LEAD_STATUS_COLORS[lead.status] ?? 'badge-neutral'}>
                              {LEAD_STATUS_LABELS[lead.status] ?? lead.status}
                            </span>
                          </td>
                          {isManager && (
                            <td className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                              {lead.assigned_to_name ?? '—'}
                            </td>
                          )}
                          <td className="text-sm tabular-nums" style={{ color: 'var(--text-secondary)' }}>
                            {formatDate(closedTs)}
                          </td>
                          <td className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                            {timeAgo(closedTs)}
                          </td>
                          <td onClick={e => e.stopPropagation()}>
                            <button
                              className="btn-icon"
                              title="View full history"
                              onClick={() => setSelectedLeadId(lead.id)}
                              style={{ color: 'var(--text-tertiary)' }}
                            >
                              <History className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Mobile expandable cards (below md) ──────────────────── */}
            <MobileHistoryCards
              leads={leads}
              isManager={isManager}
              onView={id => window.location.href = `/leads/${id}`}
              onHistory={id => setSelectedLeadId(id)}
            />
          </>
        )}
      </div>

      {/* ── Pagination ─────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <div className="shrink-0 flex items-center justify-between pt-3">
          <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
            Page {page} of {totalPages} · {total} total
          </p>
          <div className="flex gap-2">
            <button
              className="btn-secondary"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
              style={{ fontSize: 12, height: 30 }}
            >
              Previous
            </button>
            <button
              className="btn-secondary"
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
              style={{ fontSize: 12, height: 30 }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* ── EnquiryTrackingCard slide-over ─────────────────────────────── */}
      {selectedLeadId !== null && (
        <>
          <div
            className="modal-overlay"
            onClick={() => setSelectedLeadId(null)}
            style={{ zIndex: 40 }}
          />
          <div style={{
            position: 'fixed', top: 0, right: 0, bottom: 0,
            width: 480, maxWidth: '100vw', zIndex: 50,
            background: 'var(--surface-card)',
            boxShadow: '-8px 0 40px rgba(0,0,0,0.35)',
            display: 'flex', flexDirection: 'column',
          }}>
            <div
              className="flex items-center justify-between px-5 py-4 shrink-0"
              style={{ borderBottom: '1px solid var(--border-default)' }}
            >
              <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                Lead #{selectedLeadId} — Full History
              </p>
              <button className="btn-icon" onClick={() => setSelectedLeadId(null)}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <EnquiryTrackingCard leadId={selectedLeadId} />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
